import { ChakraProvider, extendTheme, ToastProviderProps } from '@chakra-ui/react';
import { useMemo } from 'react';
import { BLACK, WHITE } from 'src/constants/ui';
import { defaultFontFamily } from 'src/utils/dynamicFonts';

const DEFAULT_FONT_FAMILY = defaultFontFamily;

// Onboarding theme colors
const ONBOARDING_GRADIENT_START = '#104547';
const ONBOARDING_GRADIENT_END = '#7BC9CC';
const ONBOARDING_PRIMARY = '#17e098';
const ONBOARDING_TEXT = '#FFFFFF';
const ONBOARDING_BUTTON_TEXT = '#191d32'; // Dark text for green button
const ONBOARDING_SECONDARY_TEXT = '#FFFFFF'; // White text for secondary elements

const OnboardingThemeProvider = ({ children }: { children: React.ReactNode }): JSX.Element => {
  const theme = useMemo(() => {
    const overrides = {
      name: 'onboarding-theme',
      styles: {
        global: {
          body: {
            color: ONBOARDING_TEXT,
            bgGradient: `linear(135deg, ${ONBOARDING_GRADIENT_START} -25.92%, #165F62 35.61%, #357E81 65.95%, ${ONBOARDING_GRADIENT_END} 93.64%)`,
            fontFamily: DEFAULT_FONT_FAMILY,
          },
        },
      },
      fonts: {
        heading: DEFAULT_FONT_FAMILY,
        body: DEFAULT_FONT_FAMILY,
        font_1: DEFAULT_FONT_FAMILY,
        font_2: DEFAULT_FONT_FAMILY,
      },
      border_radius: {
        border_radius_1: '6px',
        border_radius_2: '4px',
      },
      colors: {
        text: {
          50: '#fafafa',
          100: '#f5f5f5',
          200: '#e5e5e5',
          300: '#d4d4d4',
          400: '#a3a3a3',
          500: ONBOARDING_TEXT,
          600: '#525252',
          700: '#404040',
          800: '#262626',
          900: '#171717',
          950: '#0a0a0a',
        },
        mainBG: 'transparent',
        formBG: 'transparent', // Forms have transparent backgrounds in onboarding
        mainText: ONBOARDING_TEXT,
        secondaryText: ONBOARDING_SECONDARY_TEXT,
        navBG: 'transparent',
        navText: ONBOARDING_TEXT,
        primary: {
          50: '#eafff5',
          100: '#ccffe5',
          200: '#9dfdd2',
          300: '#5ef7bb',
          400: ONBOARDING_PRIMARY,
          500: '#00cf8a',
          600: '#00a971',
          700: '#00875f',
          800: '#006b4c',
          900: '#005840',
          950: '#003225',
        },
        secondary: {
          50: '#f1fcfa',
          100: '#d1f6f0',
          200: '#a4ebe2',
          300: '#6edacf',
          400: '#40c1b8',
          500: '#27a59f',
          600: '#1c8581',
          700: '#1b6a68',
          800: '#1a5554',
          900: '#1a4747',
          950: '#082526',
        },
        // Loma Branding Colors
        lomaGreen: {
          50: '#eafff5',
          100: '#ccffe5',
          200: '#9dfdd2',
          300: '#5ef7bb',
          400: '#17e098',
          500: '#00cf8a',
          600: '#00a971',
          700: '#00875f',
          800: '#006b4c',
          900: '#005840',
          950: '#003225',
        },
        lomaDarkGreen: {
          50: '#f1fcfa',
          100: '#d1f6f0',
          200: '#a4ebe2',
          300: '#6edacf',
          400: '#40c1b8',
          500: '#27a59f',
          600: '#1c8581',
          700: '#1b6a68',
          800: '#1a5554',
          900: '#1a4747',
          950: '#082526',
        },
        lomaBlueViolet: {
          50: '#eff2ff',
          100: '#dbe2fe',
          200: '#becdff',
          300: '#92adfe',
          400: '#597efc',
          500: '#3856f9',
          600: '#2235ee',
          700: '#1a22db',
          800: '#1c1eb1',
          900: '#1d218b',
          950: '#161755',
        },
      },
      components: {
        Alert: {
          baseStyle: {
            container: {
              color: BLACK,
              fontFamily: DEFAULT_FONT_FAMILY,
              borderRadius: '4px',
            },
          },
        },
        Heading: {
          baseStyle: {
            fontFamily: DEFAULT_FONT_FAMILY,
            color: ONBOARDING_TEXT,
          },
        },
        Input: {
          variants: {
            outline: {
              field: {
                bg: 'white',
                color: BLACK,
                borderRadius: '6px',
                fontFamily: DEFAULT_FONT_FAMILY,
              },
            },
          },
          defaultProps: {
            variant: 'outline',
          },
        },
        NumberInput: {
          variants: {
            outline: {
              field: {
                bg: 'white',
                color: BLACK,
                borderRadius: '6px',
                fontFamily: DEFAULT_FONT_FAMILY,
              },
            },
          },
          defaultProps: {
            variant: 'outline',
          },
        },
        Textarea: {
          variants: {
            outline: {
              bg: 'white',
              color: BLACK,
              borderRadius: '6px',
              fontFamily: DEFAULT_FONT_FAMILY,
            },
          },
          defaultProps: {
            variant: 'outline',
          },
        },
        FormLabel: {
          baseStyle: {
            fontFamily: DEFAULT_FONT_FAMILY,
            color: ONBOARDING_TEXT,
          },
        },
        Menu: {
          baseStyle: {
            item: {
              color: BLACK,
              fontFamily: DEFAULT_FONT_FAMILY,
            },
          },
        },
        ButtonGroup: {
          baseStyle: {
            Button: {
              color: BLACK,
              borderRadius: '6px',
              fontFamily: DEFAULT_FONT_FAMILY,
            },
          },
        },
        Button: {
          baseStyle: {
            color: BLACK,
            borderRadius: '6px',
            fontFamily: DEFAULT_FONT_FAMILY,
          },
          variants: {
            solid: {
              bg: ONBOARDING_PRIMARY,
              color: ONBOARDING_BUTTON_TEXT,
              borderRadius: 'full',
              fontWeight: 'semibold',
              boxShadow: '0 4px 10.5px 3px rgba(71, 49, 34, 0.14)',
              _hover: {
                bg: `${ONBOARDING_PRIMARY}cc`, // 80% opacity
                _disabled: {
                  bg: ONBOARDING_PRIMARY,
                },
              },
              _active: {
                bg: `${ONBOARDING_PRIMARY}b3`, // 70% opacity
              },
            },
          },
        },
        Select: {
          variants: {
            outline: {
              field: {
                bg: 'white',
                color: BLACK,
                borderRadius: '6px',
                fontFamily: DEFAULT_FONT_FAMILY,
              },
              icon: {
                color: BLACK,
              },
            },
          },
          defaultProps: {
            variant: 'outline',
          },
        },
        Drawer: {
          baseStyle: {
            dialog: {
              color: BLACK,
              fontFamily: DEFAULT_FONT_FAMILY,
            },
          },
        },
        Tag: {
          baseStyle: {
            container: {
              fontFamily: DEFAULT_FONT_FAMILY,
            },
            label: {
              textTransform: 'uppercase',
            },
          },
        },
        Popover: {
          baseStyle: {
            content: {
              color: BLACK,
              borderRadius: '4px',
              fontFamily: DEFAULT_FONT_FAMILY,
            },
          },
        },
        Breadcrumb: {
          baseStyle: {
            item: {
              fontFamily: DEFAULT_FONT_FAMILY,
            },
          },
        },
        Modal: {
          baseStyle: {
            dialog: {
              borderRadius: '4px',
              bg: WHITE,
              color: BLACK,
              fontFamily: DEFAULT_FONT_FAMILY,
            },
          },
        },
        Card: {
          baseStyle: {
            container: {
              borderRadius: '4px',
            },
          },
        },
        Text: {
          baseStyle: {
            color: ONBOARDING_TEXT,
          },
        },
        Divider: {
          baseStyle: {
            borderColor: 'whiteAlpha.400',
          },
        },
      },
    };
    return extendTheme(overrides);
  }, []);

  const toastOptions = useMemo(() => {
    return {
      defaultOptions: {
        containerStyle: {
          borderRadius: '4px',
          fontFamily: DEFAULT_FONT_FAMILY,
        },
      },
    } as ToastProviderProps;
  }, []);

  return (
    <ChakraProvider theme={theme} toastOptions={toastOptions}>
      {children}
    </ChakraProvider>
  );
};

export default OnboardingThemeProvider;
